import { Box, Typography } from "@mui/material";
import { useRef } from "react";
import { useHistory } from "react-router-dom";
import { DetailTable } from "../components/DetailTable";
import { FilterBar } from "../components/FilterBar";
import { FilterChip } from "../components/FilterChip";
import { HistoryModal } from "../components/HistoryModal";
import { KpiCards } from "../components/KpiCards";
import { LineChart } from "../components/LineChart";
import { RankingPanel } from "../components/RankingPanel";
import { StageChart } from "../components/StageChart";
import { StaleProjectsList } from "../components/StaleProjectsList";
import { TE_COLORS } from "../components/theme";
import { useTimeEfficiencyDashboard } from "../hooks/useTimeEfficiencyDashboard";
import { useTimeEfficiencyRealData } from "../hooks/useTimeEfficiencyRealData";
import { exportTableRowsToCsv } from "../utils/exportCsv";

/**
 * #MMV-2615 -- layout 100% igual ao porte do mockup (não muda); só a fonte
 * dos VALORES muda, painel a painel:
 * - REAL (Api, `useTimeEfficiencyRealData`): barra de filtros, os 3 KPIs
 *   originais, gráfico de etapas, lista de parados, tabela detalhada.
 * - MOCK (`useTimeEfficiencyDashboard`, `data/mockData.ts`): os 3 KPIs
 *   extras com drill-down, gráfico de linha, rankings por célula/
 *   coordenador/tipo de evento -- a Api ainda não tem endpoint pra isso.
 */
export function TimeEfficiencyDashboardPage() {
  const history = useHistory();
  const mock = useTimeEfficiencyDashboard();
  // #MMV-2615: "Projetos acima da média", "Etapa com mais projetos parados
  // agora" e "Projetos com reabertura" já são reais -- quando um desses
  // cards está selecionado, a tabela abaixo troca/filtra de acordo (Api).
  const aboveAverageActive = mock.drillDown?.type === "acimaMedia";
  const etapaParadaActive = mock.drillDown?.type === "etapaParada";
  const etapaLentaActive = mock.drillDown?.type === "etapaLenta";
  const reopenActive = mock.drillDown?.type === "reabertura";
  const real = useTimeEfficiencyRealData({
    aboveAverageActive,
    etapaParadaActive,
    etapaLentaActive,
    reopenActive,
  });

  const detailTableRef = useRef<HTMLDivElement>(null);
  function scrollToTable() {
    detailTableRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
  function handleAcimaMediaClick() {
    const willActivate = !aboveAverageActive;
    mock.kpiClickAcimaMedia();
    if (willActivate) scrollToTable();
  }
  function handleEtapaParadaClick() {
    if (!real.presoAgora) return;
    const willActivate = !(
      etapaParadaActive && mock.drillDown?.etapa === real.presoAgora.etapa
    );
    mock.toggleDrillDown({ type: "etapaParada", etapa: real.presoAgora.etapa });
    if (willActivate) scrollToTable();
  }
  function handleEtapaLentaClick() {
    if (!real.kpis.slowest) return;
    const willActivate = !(
      etapaLentaActive && mock.drillDown?.etapa === real.kpis.slowest.etapa
    );
    mock.toggleDrillDown({
      type: "etapaLenta",
      etapa: real.kpis.slowest.etapa,
    });
    if (willActivate) scrollToTable();
  }
  function handleReaberturaClick() {
    const willActivate = !reopenActive;
    mock.toggleDrillDown({ type: "reabertura" });
    if (willActivate) scrollToTable();
  }
  function handleOpenProject(projectId: string) {
    history.push(`/projeto?projectId=${projectId}`);
  }
  function handleClearFilters() {
    // Limpa o drill-down também -- senão ele ficava ativo e reaplicava o
    // próprio filtro (status, por exemplo) na hora, como se "Limpar" não
    // tivesse limpado tudo.
    mock.clearDrillDown();
    real.clearFilters();
  }

  const kpis = real.kpis;

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          py: 3,
          px: 3,
          bgcolor: TE_COLORS.bg,
        }}
      >
        <Box sx={{ maxWidth: 1560, mx: "auto" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2.25}
            flexWrap="wrap"
            gap={1.5}
          >
            <Box>
              <Typography
                sx={{ fontSize: 21, fontWeight: 700, color: TE_COLORS.text }}
              >
                Tempo e Eficiência dos Projetos
              </Typography>
              <Typography
                sx={{ fontSize: 13, color: TE_COLORS.textDim, mt: 0.5 }}
              >
                Análise do tempo total dos projetos e performance por etapa do
                fluxo MMIDD.
              </Typography>
            </Box>
          </Box>

          <FilterBar
            celulaOptions={real.celulaOptions}
            coordenadorOptions={real.coordenadorOptions}
            tipoEventoOptions={real.tipoEventoOptions}
            statusOptions={real.statusOptions}
            filtroCelula={real.filtroCelula}
            filtroCoordenador={real.filtroCoordenador}
            filtroTipoEvento={real.filtroTipoEvento}
            filtroStatus={real.filtroStatus}
            filtroCampoData={real.filtroCampoData}
            dataInicio={real.dataInicio}
            dataFim={real.dataFim}
            onCelula={real.setFiltroCelula}
            onCoordenador={real.setFiltroCoordenador}
            onTipoEvento={real.setFiltroTipoEvento}
            onStatus={real.setFiltroStatus}
            onCampoData={real.setFiltroCampoData}
            onDataInicio={real.setDataInicio}
            onDataFim={real.setDataFim}
            onApply={real.applyFilters}
            onClear={handleClearFilters}
          />

          <KpiCards
            kpis={kpis}
            drillType={mock.drillDown?.type ?? null}
            onEtapaLenta={handleEtapaLentaClick}
            onAcimaMedia={handleAcimaMediaClick}
            onEtapaParada={handleEtapaParadaClick}
            onReabertura={handleReaberturaClick}
            kpisLoading={real.kpisLoading}
            presoAgoraLoading={real.stageChartLoading}
            reaberturaLoading={real.reopenKpiLoading}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "1.05fr 1.15fr 0.9fr" },
              gap: 2,
              mb: 2.5,
              alignItems: "start",
            }}
          >
            <StageChart
              data={real.stageChartData}
              loading={real.stageChartLoading}
            />
            <LineChart />
            <StaleProjectsList
              items={real.staleProjects}
              total={real.staleProjects.length}
              expanded={false}
              onToggleExpanded={() => {}}
              loading={real.staleLoading}
            />
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <RankingPanel
              title="Tempo médio total por célula"
              description="Combina projetos concluídos e em andamento; ordenado do maior tempo médio para o menor."
              items={real.celulaRanking}
              emptyMessage="Nenhum projeto no filtro selecionado."
              loading={real.rankingsLoading}
            />
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <RankingPanel
              title="Ranking por coordenador"
              description='Top 10 coordenadores com o projeto parado há mais tempo em andamento ("Coordenador 1 da Célula 1").'
              items={real.coordenadorRanking}
              emptyMessage={
                real.coordenadorHasAnyData
                  ? "Nenhum projeto com coordenador no filtro/drill-down atual."
                  : "Sem dado de coordenador disponível (carregue um relatório com essa coluna preenchida)."
              }
              loading={real.rankingsLoading}
            />
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <RankingPanel
              title="Ranking por tipo de evento"
              description="Combina projetos concluídos e em andamento; ordenado do maior tempo médio total para o menor."
              items={real.tipoEventoRanking}
              emptyMessage={
                real.tipoEventoHasAnyData
                  ? "Nenhum projeto com tipo de evento no filtro/drill-down atual."
                  : "Sem dado de tipo de evento disponível (carregue um relatório com essa coluna preenchida)."
              }
              loading={real.rankingsLoading}
            />
          </Box>

          <Box ref={detailTableRef}>
            <FilterChip
              drillDown={mock.drillDown}
              onClear={mock.clearDrillDown}
            />
          </Box>

          <DetailTable
            rows={real.tableRows}
            totalCount={real.totalRows}
            searchTerm={real.searchTerm}
            onSearch={real.setSearchTerm}
            sortKey={real.sortKey}
            sortDir={real.sortDir}
            onToggleSort={real.toggleSort}
            currentPage={real.currentPage}
            totalPages={real.totalPages}
            onPrevPage={real.goToPrevPage}
            onNextPage={real.goToNextPage}
            onOpenHistory={real.openHistory}
            onOpenProject={handleOpenProject}
            onExport={() => exportTableRowsToCsv(real.tableRows)}
            tempoTotalAvailable={real.tempoTotalAvailable}
            reaberturasAvailable={real.reaberturasAvailable}
            mode={
              reopenActive
                ? "reopen"
                : aboveAverageActive
                ? "aboveAverage"
                : etapaParadaActive || etapaLentaActive
                ? "stageFilter"
                : "default"
            }
            loading={real.projectsFetching}
          />

          <HistoryModal
            open={Boolean(real.historyProjectId)}
            projectId={real.historyDisplayId}
            sequencia={real.historySequencia}
            loading={real.historyLoading}
            onClose={real.closeHistory}
          />

          <Box
            component="footer"
            display="flex"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            mt={2}
            pt={1.75}
            sx={{ color: TE_COLORS.textFaint, fontSize: 11 }}
          >
            <span>
              Os cálculos consideram apenas o log de eventos do MMIDD; tempo em
              Devolvido (pendência) aparece separado do tempo de etapa.
            </span>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
